import { useCallback, useEffect, useRef, useState } from "react";

import {
  clearStoredResendCooldown,
  getRemainingCooldownSeconds,
  getStoredResendCooldownEndsAt,
  setStoredResendCooldownEndsAt,
  RESEND_COOLDOWN_MS
} from "./useSignatureResend.helpers";

import {
  IDocumentEletronicSignature,
  LEGAL_REPRESENTATIVE_TYPES
} from "@/modules/Terms/interfaces/ITerm.interface";

interface UseSignatureResendOptions {
  electronicSignature?: IDocumentEletronicSignature;
  onResend: () => Promise<{ error?: unknown }>;
  cooldownKey?: string;
}

const COOLDOWN_TICK_MS = 1000;
const DEFAULT_COOLDOWN_KEY = "signature-resend";

export const useSignatureResend = ({
  electronicSignature,
  onResend,
  cooldownKey = DEFAULT_COOLDOWN_KEY
}: UseSignatureResendOptions) => {
  const [canResendSignatureRequest, setCanResendSignatureRequest] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [remainingCooldownSeconds, setRemainingCooldownSeconds] = useState(() =>
    getRemainingCooldownSeconds(getStoredResendCooldownEndsAt(cooldownKey))
  );
  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const defaultCanResendLogic = useCallback(
    (signature: IDocumentEletronicSignature) => {
      const isDocumentRunning = signature?.document?.status === "running";
      const documentHasSigners = signature?.document?.signers?.length > 0;

      const hasPendingAccountSignature = signature?.document?.signers.some(
        signer =>
          !signer.signature &&
          !LEGAL_REPRESENTATIVE_TYPES.includes(signer.sign_as)
      );

      return (
        isDocumentRunning && documentHasSigners && hasPendingAccountSignature
      );
    },
    []
  );

  useEffect(() => {
    if (!electronicSignature) return;

    const checkFunction = defaultCanResendLogic;
    setCanResendSignatureRequest(checkFunction(electronicSignature));
  }, [electronicSignature, defaultCanResendLogic]);

  const clearCooldownInterval = useCallback(() => {
    if (!cooldownIntervalRef.current) return;

    clearInterval(cooldownIntervalRef.current);
    cooldownIntervalRef.current = null;
  }, []);

  const syncCooldown = useCallback(
    (cooldownEndsAt: number | null) => {
      const updateRemainingCooldown = () => {
        const nextRemainingSeconds =
          getRemainingCooldownSeconds(cooldownEndsAt);

        setRemainingCooldownSeconds(nextRemainingSeconds);

        if (nextRemainingSeconds <= 0) {
          clearCooldownInterval();
          clearStoredResendCooldown(cooldownKey);
        }

        return nextRemainingSeconds;
      };

      clearCooldownInterval();

      if (!cooldownEndsAt) {
        setRemainingCooldownSeconds(0);

        return;
      }

      if (updateRemainingCooldown() > 0) {
        cooldownIntervalRef.current = setInterval(
          updateRemainingCooldown,
          COOLDOWN_TICK_MS
        );
      }
    },
    [clearCooldownInterval, cooldownKey]
  );

  const startCooldown = useCallback(() => {
    const cooldownEndsAt = Date.now() + RESEND_COOLDOWN_MS;

    setStoredResendCooldownEndsAt(cooldownKey, cooldownEndsAt);
    syncCooldown(cooldownEndsAt);
  }, [cooldownKey, syncCooldown]);

  useEffect(() => {
    return clearCooldownInterval;
  }, [clearCooldownInterval]);

  useEffect(() => {
    syncCooldown(getStoredResendCooldownEndsAt(cooldownKey));
  }, [cooldownKey, syncCooldown]);

  const handleResend = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await onResend();

      if (!response.error) {
        startCooldown();
      }
    } finally {
      setIsLoading(false);
    }
  }, [onResend, startCooldown]);

  const isCooldownActive = remainingCooldownSeconds > 0;

  const isButtonDisabled =
    !canResendSignatureRequest || isLoading || isCooldownActive;

  return {
    canResendSignatureRequest,
    isLoading,
    isCooldownActive,
    isButtonDisabled,
    startCooldown,
    handleResend
  };
};
